import { useCallback } from 'react';
import { useSnackbar } from '../../../shared/utils/snackbar';
import { useAuth } from '../../auth/useAuth';
import {
  cancelLeaveRequest,
  createLeaveRequest,
  getPrimaryLocationForCoach,
  LeaveRequestAlreadyDecidedError,
  recomputeAllocationSnapshot,
  updateLeaveRequestStatusWithOutcome,
  requestLeaveChange,
} from '../services/leaveService';
import {
  CreateLeaveRequestInput,
  LeaveRequestRecord,
  LeaveRequestStatus,
  UpdateLeaveStatusInput,
} from '../types';
import { createNotification } from '../../../shared/utils/notifications';

interface SubmitLeaveRequestParams {
  startDate: Date;
  endDate: Date;
  reason: string;
  comment?: string;
}

interface UpdateStatusParams {
  request: LeaveRequestRecord;
  status: LeaveRequestStatus;
  note?: string;
  managerComment?: string;
}

interface UseLeaveActionsResult {
  submitLeaveRequest: (params: SubmitLeaveRequestParams) => Promise<string | null>;
  approveLeaveRequest: (params: UpdateStatusParams) => Promise<void>;
  denyLeaveRequest: (params: UpdateStatusParams) => Promise<void>;
  cancelOwnRequest: (request: LeaveRequestRecord, note?: string) => Promise<void>;
  requestChange: (request: LeaveRequestRecord, params: SubmitLeaveRequestParams) => Promise<string | null>;
}

const DEFAULT_APPROVED_NOTE = 'Request approved';
const DEFAULT_DENIED_NOTE = 'Request denied';

export const useLeaveActions = (): UseLeaveActionsResult => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, userMetadata, userRole } = useAuth();

  const submitLeaveRequest = useCallback(
    async ({ startDate, endDate, reason, comment }: SubmitLeaveRequestParams) => {
      if (!user || !userMetadata) {
        enqueueSnackbar('You need to be signed in to submit leave.', { variant: 'warning' });
        return null;
      }

      if (!startDate || !endDate) {
        enqueueSnackbar('Select both start and end dates.', { variant: 'warning' });
        return null;
      }

      try {
        const location = await getPrimaryLocationForCoach(userMetadata.coach_id || null);
        const staffInfo: CreateLeaveRequestInput = {
          staff: {
            uid: user.uid,
            displayName: userMetadata.displayName || user.displayName || 'Unknown',
            coachId: userMetadata.coach_id || null,
            email: userMetadata.email,
            location,
          },
          period: {
            startDate,
            endDate,
          },
          reason,
          staffComment: comment,
        };

        const requestId = await createLeaveRequest(staffInfo);

        const year = startDate.getFullYear();
        await recomputeAllocationSnapshot(
          user.uid,
          year,
          {
            staffName: staffInfo.staff.displayName,
            coachId: staffInfo.staff.coachId,
            location,
          }
        );

        await createNotification({
          type: 'leave_request_submitted',
          clientId: staffInfo.staff.uid,
          clientName: staffInfo.staff.displayName,
          message: `${staffInfo.staff.displayName} submitted a leave request (${reason}).`,
          metadata: {
            submittedByName: staffInfo.staff.displayName,
            submittedBy: staffInfo.staff.uid,
            staffUid: staffInfo.staff.uid,
            staffName: staffInfo.staff.displayName,
          },
          // The requester is not a recipient of their own request.
          recipients: {
            roles: ['MANAGER', 'ADMIN'],
            locations: location ? [location] : [],
          },
          managerLocations: location ? [location] : [],
          submittedBy: {
            name: staffInfo.staff.displayName,
            uid: staffInfo.staff.uid,
            role: 'USER',
          },
          eventKey: requestId,
        });

        enqueueSnackbar('Leave request submitted for approval.', { variant: 'success' });
        return requestId;
      } catch (error) {
        console.error('Error submitting leave request:', error);
        enqueueSnackbar('Unable to submit leave request. Please try again later.', { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar, user, userMetadata]
  );

  const handleStatusUpdate = useCallback(
    async ({ request, status, note, managerComment }: UpdateStatusParams) => {
      if (!user || !userMetadata) {
        enqueueSnackbar('You need to be signed in to manage leave.', { variant: 'warning' });
        return;
      }

      const changedBy: UpdateLeaveStatusInput['changedBy'] = {
        uid: user.uid,
        displayName: userMetadata.displayName || user.displayName || 'Manager',
        role: (userRole as 'USER' | 'MANAGER' | 'ADMIN') || 'USER',
      };
      const actorName = changedBy.displayName || 'Manager';

      try {
        const { record: updatedRequest, written } = await updateLeaveRequestStatusWithOutcome({
          requestId: request.id,
          status,
          changedBy,
          note,
          managerComment,
        });

				// Recompute allocation for both change and original years when applicable.
				const years = new Set<number>();
				if (updatedRequest.period?.year) {
					years.add(updatedRequest.period.year);
				}
				if (updatedRequest.change?.originalYear) {
					years.add(updatedRequest.change.originalYear);
				}
				if (years.size === 0) {
					years.add(new Date().getFullYear());
				}

				await Promise.all(
					Array.from(years).map((year) =>
						recomputeAllocationSnapshot(updatedRequest.staff.uid, year, {
							staffName: updatedRequest.staff.displayName,
							coachId: updatedRequest.staff.coachId || null,
							location: updatedRequest.visibility?.staffLocation || null,
						})
					)
				);

        // The same decision submitted twice: the requester was notified the first time.
        if (!written) {
          enqueueSnackbar(
            `${updatedRequest.staff.displayName}’s request was already ${status.toLowerCase()}.`,
            { variant: 'info' }
          );
          return;
        }

        await createNotification({
          type: 'leave_status_update',
          clientId: updatedRequest.staff.uid,
          clientName: updatedRequest.staff.displayName,
          message: `${actorName} ${status.toLowerCase()} the leave request for ${updatedRequest.staff.displayName}.`,
          metadata: {
            submittedByName: actorName,
            submittedBy: changedBy.uid,
            staffUid: updatedRequest.staff.uid,
            staffName: updatedRequest.staff.displayName,
            checkinType: status.toLowerCase(),
          },
          recipients: {
            coachIds: updatedRequest.staff.coachId ? [String(updatedRequest.staff.coachId)] : [],
            roles: ['ADMIN', 'MANAGER'],
            locations: updatedRequest.visibility?.managerLocations || [],
          },
          leadCoachId: updatedRequest.staff.coachId || null,
          managerLocations: updatedRequest.visibility?.managerLocations || [],
          submittedBy: {
            name: actorName,
            uid: changedBy.uid,
            role: changedBy.role,
          },
          eventKey: updatedRequest.id,
        });

        enqueueSnackbar(`Request marked as ${status.toLowerCase()}.`, { variant: 'success' });
      } catch (error) {
        if (error instanceof LeaveRequestAlreadyDecidedError) {
          enqueueSnackbar(
            `${request.staff?.displayName || 'This'}’s request was already ${String(error.currentStatus ?? 'decided').toLowerCase()}, so nothing was changed.`,
            { variant: 'info' }
          );
          return;
        }
        console.error('Error updating leave request status:', error);
        enqueueSnackbar('Unable to update request. Please try again.', { variant: 'error' });
      }
    },
    [enqueueSnackbar, user, userMetadata, userRole]
  );

  const approveLeaveRequest = useCallback(
    async (params: UpdateStatusParams) => {
      await handleStatusUpdate({
        ...params,
        status: 'APPROVED',
        note: params.note || DEFAULT_APPROVED_NOTE,
      });
    },
    [handleStatusUpdate]
  );

  const denyLeaveRequest = useCallback(
    async (params: UpdateStatusParams) => {
      await handleStatusUpdate({
        ...params,
        status: 'DENIED',
        note: params.note || DEFAULT_DENIED_NOTE,
      });
    },
    [handleStatusUpdate]
  );

  const cancelOwnRequest = useCallback(
    async (request: LeaveRequestRecord, note?: string) => {
      if (!user || !userMetadata) {
        enqueueSnackbar('You need to be signed in to manage leave.', { variant: 'warning' });
        return;
      }

      try {
        const cancelled = await cancelLeaveRequest(
          request.id,
          {
            uid: user.uid,
            displayName: userMetadata.displayName || user.displayName || 'User',
            role: (userRole as 'USER' | 'MANAGER' | 'ADMIN') || 'USER',
          },
          note
        );

				// Recompute allocation for both change and original years when applicable.
				const years = new Set<number>();
				if (cancelled.period?.year) {
					years.add(cancelled.period.year);
				}
				if (cancelled.change?.originalYear) {
					years.add(cancelled.change.originalYear);
				}
				if (years.size === 0) {
					years.add(new Date().getFullYear());
				}

				await Promise.all(
					Array.from(years).map((year) =>
						recomputeAllocationSnapshot(cancelled.staff.uid, year, {
							staffName: cancelled.staff.displayName,
							coachId: cancelled.staff.coachId || null,
							location: cancelled.visibility?.staffLocation || null,
						})
					)
				);

        await createNotification({
          type: 'leave_status_update',
          clientId: cancelled.staff.uid,
          clientName: cancelled.staff.displayName,
          message: `${cancelled.staff.displayName} cancelled their leave request.`,
          metadata: {
            submittedByName: cancelled.staff.displayName || undefined,
            submittedBy: cancelled.staff.uid,
            staffUid: cancelled.staff.uid,
            staffName: cancelled.staff.displayName,
            checkinType: 'cancelled',
          },
          recipients: {
            roles: ['ADMIN', 'MANAGER'],
            locations: cancelled.visibility?.managerLocations || [],
          },
          managerLocations: cancelled.visibility?.managerLocations || [],
          submittedBy: {
            name: cancelled.staff.displayName,
            uid: cancelled.staff.uid,
            role: 'USER',
          },
          eventKey: cancelled.id,
        });

        enqueueSnackbar('Request cancelled.', { variant: 'success' });
      } catch (error) {
        console.error('Error cancelling request:', error);
        enqueueSnackbar('Unable to cancel request. Please try again later.', { variant: 'error' });
      }
    },
    [enqueueSnackbar, user, userMetadata]
  );

  const requestChange = useCallback(
    async (request: LeaveRequestRecord, params: SubmitLeaveRequestParams) => {
      if (!user || !userMetadata) {
        enqueueSnackbar('You need to be signed in to request a change.', { variant: 'warning' });
        return null;
      }

      try {
        const location = await getPrimaryLocationForCoach(userMetadata.coach_id || null);
        const changeInput: CreateLeaveRequestInput = {
          staff: {
            uid: request.staff.uid,
            displayName: request.staff.displayName,
            coachId: request.staff.coachId || null,
            email: userMetadata.email,
            location,
          },
          manager: request.manager,
          period: {
            startDate: params.startDate,
            endDate: params.endDate,
          },
          reason: params.reason,
          staffComment: params.comment,
          originalRequestId: request.id,
        };

        const changeRequestId = await requestLeaveChange(request.id, changeInput);

				// Recompute allocation for both change and original years when applicable.
				const years = new Set<number>();
				years.add(params.startDate.getFullYear());
				if (request.period?.year) {
					years.add(request.period.year);
				}

				await Promise.all(
					Array.from(years).map((year) =>
						recomputeAllocationSnapshot(request.staff.uid, year, {
							staffName: request.staff.displayName,
							coachId: request.staff.coachId || null,
							location,
						})
					)
				);

        await createNotification({
          type: 'leave_request_submitted',
          clientId: request.staff.uid,
          clientName: request.staff.displayName,
          message: `${request.staff.displayName} requested changes to an existing leave.`,
          metadata: {
            submittedByName: request.staff.displayName || undefined,
            submittedBy: request.staff.uid,
            staffUid: request.staff.uid,
            staffName: request.staff.displayName,
          },
          recipients: {
            roles: ['MANAGER', 'ADMIN'],
            locations: location ? [location] : request.visibility?.managerLocations || [],
          },
          managerLocations: location ? [location] : request.visibility?.managerLocations || [],
          submittedBy: {
            name: request.staff.displayName,
            uid: request.staff.uid,
            role: 'USER',
          },
          eventKey: changeRequestId,
        });

        enqueueSnackbar('Change request submitted and pending approval.', { variant: 'info' });
        return changeRequestId;
      } catch (error) {
        console.error('Error creating change request:', error);
        enqueueSnackbar('Unable to request changes right now.', { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar, user, userMetadata]
  );

  return {
    submitLeaveRequest,
    approveLeaveRequest,
    denyLeaveRequest,
    cancelOwnRequest,
    requestChange,
  };
};

export default useLeaveActions;
